import { useEffect, useState } from "react";
import { AlertCircle, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUpdateComplaintDraft } from "@/hooks/useComplaints";
import { fetchBrands, type BrandRef } from "@/api/businessProfile";
import type {
  ComplaintDetailResponse,
  ComplaintDraftUpdatePayload,
  ComplaintProductType,
  ComplaintType,
} from "@/api/types";
import {
  CONDITIONAL_REQUIRED_TYPES,
  OPTIONAL_PRODUCT_TYPES,
  PRODUCT_LINKED_TYPES,
  PRODUCT_TYPE_LABEL,
  TYPE_LABEL,
} from "./constants";

const TYPES: ComplaintType[] = [
  "simple_service", "document_error", "delivery_logistics", "order_error",
  "pricing_terms", "packaging_damage_no_quality", "product_quality", "food_quality",
  "batch_issue", "expiry_issue", "health_risk", "recurring_sku_issue", "other",
];
const PRODUCT_TYPES: ComplaintProductType[] = [
  "food", "treats", "litter", "hygiene", "accessory", "veterinary_product", "other",
];
const BRAND_OTHER = "__other__";

/**
 * COMP-S3 W5/W6/W7d — editable draft form on the detail drawer (was read-only).
 * The single place the product block + description + type are completed after
 * create (the recon dead-end). Saves via updateDraft (PUT); 409 conflict bubbles
 * to onConflict. Upload (evidence) lives in the sibling AttachmentsPanel.
 */
export function DraftEditForm({
  c,
  onConflict,
}: {
  c: ComplaintDetailResponse;
  onConflict: () => void;
}) {
  const mut = useUpdateComplaintDraft(c.id);

  const [type, setType] = useState<ComplaintType>((c.complaintType || "simple_service") as ComplaintType);
  const [linkProduct, setLinkProduct] = useState(c.product.linkedToProduct);
  const [summary, setSummary] = useState(c.summary);
  const [description, setDescription] = useState(c.description);
  const [productType, setProductType] = useState<ComplaintProductType | "">(c.product.productType ?? "");
  const [article, setArticle] = useState(c.product.article ?? "");
  const [productName, setProductName] = useState(c.product.name ?? "");
  const [batch, setBatch] = useState(c.product.batch ?? "");
  const [batchUnknown, setBatchUnknown] = useState(c.product.batchUnknown);
  const [expiry, setExpiry] = useState(c.product.expiryDate ?? "");
  const [affectedQty, setAffectedQty] = useState(c.product.affectedQuantity ?? "");

  const [brands, setBrands] = useState<BrandRef[]>([]);
  const [brandsDown, setBrandsDown] = useState(false);
  const [brandSel, setBrandSel] = useState(
    c.product.brand.biBrandId ? c.product.brand.biBrandId : c.product.brand.unverified ? BRAND_OTHER : "",
  );
  const [brandFree, setBrandFree] = useState(c.product.brand.unverified ? (c.product.brand.name ?? "") : "");

  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const showProduct =
    PRODUCT_LINKED_TYPES.includes(type) || (OPTIONAL_PRODUCT_TYPES.includes(type) && linkProduct);
  const isCriticalProduct = CONDITIONAL_REQUIRED_TYPES.includes(type);
  const brandUnverified = brandSel === BRAND_OTHER || (brandsDown && brandSel === "");

  useEffect(() => {
    if (!showProduct || brands.length > 0 || brandsDown) return;
    const ctrl = new AbortController();
    fetchBrands(undefined, ctrl.signal)
      .then((r) => setBrands(r.items.filter((b) => !b.inactive)))
      .catch(() => setBrandsDown(true));
    return () => ctrl.abort();
  }, [showProduct, brands.length, brandsDown]);

  function brandPayload(): Pick<ComplaintDraftUpdatePayload, "cCBrandBiId" | "cCBrand" | "cCBrandUnverified"> {
    if (brandSel && brandSel !== BRAND_OTHER) return { cCBrandBiId: brandSel, cCBrandUnverified: false };
    if (brandUnverified && brandFree.trim()) return { cCBrand: brandFree.trim(), cCBrandUnverified: true };
    return { cCBrand: "", cCBrandUnverified: false };
  }

  async function save() {
    setError(null);
    const body: ComplaintDraftUpdatePayload = {
      expectedModifiedAt: c.modifiedAt,
      complaintType: type,
      summary: summary.trim(),
      description: description.trim(),
      cCLinkedToProduct: OPTIONAL_PRODUCT_TYPES.includes(type) ? linkProduct : undefined,
      ...(showProduct
        ? {
            cCProductType: (productType || "") as ComplaintProductType | "",
            cCProductArticle: article.trim(),
            cCProductName: productName.trim(),
            ...brandPayload(),
            cCBatch: batch.trim(),
            cCBatchUnknown: batchUnknown,
            cCExpiryDate: expiry,
            cCAffectedQuantity: affectedQty.trim(),
          }
        : {}),
    };
    try {
      await mut.mutateAsync(body);
      setSavedAt(Date.now());
    } catch (e) {
      const msg = (e as Error)?.message ?? "";
      if (/draft_conflict|409/i.test(msg)) {
        onConflict();
        return;
      }
      setError(msg || "Не вдалося зберегти зміни");
    }
  }

  return (
    <section className="space-y-3 rounded-md border border-primary/30 bg-primary/[0.03] p-3">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Редагування чернетки
      </h4>

      <div>
        <label className="mb-1 block text-xs font-medium">Тип рекламації</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as ComplaintType)}
          className="h-10 w-full rounded-md border bg-background px-2 text-sm"
        >
          {TYPES.map((t) => (
            <option key={t} value={t}>{TYPE_LABEL[t]}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">Короткий опис</label>
        <input
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          maxLength={200}
          className="h-10 w-full rounded-md border bg-background px-3 text-sm"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">Детальний опис</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="w-full rounded-md border px-3 py-2 text-sm"
        />
      </div>

      {OPTIONAL_PRODUCT_TYPES.includes(type) && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={linkProduct}
            onChange={(e) => setLinkProduct(e.target.checked)}
            className="h-4 w-4 rounded border"
          />
          Пов’язано з товаром
        </label>
      )}

      {showProduct && (
        <div className="space-y-3 rounded-md border bg-muted/20 p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Контекст товару
            </div>
            {isCriticalProduct && (
              <span className="rounded bg-warning px-1.5 py-0.5 text-[10px] font-semibold text-warning-foreground">
                обов’язково на подачу
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium">Тип товару</label>
              <select
                value={productType}
                onChange={(e) => setProductType(e.target.value as ComplaintProductType | "")}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              >
                <option value="">— виберіть —</option>
                {PRODUCT_TYPES.map((p) => (
                  <option key={p} value={p}>{PRODUCT_TYPE_LABEL[p]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Артикул (SKU)</label>
              <input value={article} onChange={(e) => setArticle(e.target.value)}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium">Назва товару</label>
              <input value={productName} onChange={(e) => setProductName(e.target.value)}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium">Бренд</label>
              {brandsDown ? (
                <input
                  value={brandFree}
                  onChange={(e) => setBrandFree(e.target.value)}
                  placeholder="Бренд (каталог недоступний)"
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                />
              ) : (
                <>
                  <select
                    value={brandSel}
                    onChange={(e) => setBrandSel(e.target.value)}
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  >
                    <option value="">— виберіть бренд —</option>
                    {brands.map((b) => (
                      <option key={b.biBrandId} value={b.biBrandId}>{b.name}</option>
                    ))}
                    <option value={BRAND_OTHER}>Інший бренд (не підтверджено)…</option>
                  </select>
                  {brandSel === BRAND_OTHER && (
                    <input
                      value={brandFree}
                      onChange={(e) => setBrandFree(e.target.value)}
                      placeholder="Назва бренду (буде позначено «не підтверджено»)"
                      className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm"
                    />
                  )}
                </>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Партія</label>
              <input value={batch} onChange={(e) => setBatch(e.target.value)} disabled={batchUnknown}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm disabled:opacity-50" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Термін придатності</label>
              <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium">Кількість</label>
              <input value={affectedQty} onChange={(e) => setAffectedQty(e.target.value)}
                placeholder="3 шт / 2 коробки"
                className="h-9 w-full rounded-md border bg-background px-2 text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={batchUnknown} onChange={(e) => setBatchUnknown(e.target.checked)}
              className="h-4 w-4 rounded border" />
            Партія невідома
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={() => void save()} disabled={mut.isPending} variant="outline" className="gap-1.5">
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Зберегти зміни
        </Button>
        {savedAt && !mut.isPending && <span className="text-xs text-success-foreground">Збережено</span>}
      </div>
    </section>
  );
}
